import { assertNever } from "@msys/common";
import {
  Autocomplete,
  AutocompleteMultiple,
  defaultIsEqualArray,
  FormattedFloatInput,
  getFilterOptions,
  LabeledValue,
  LabeledValueWithButton,
  ModalOpenButton,
  useFormatting,
  useSyncState,
  VisibilityButton,
} from "@msys/ui";
import {
  Delete as DeleteIcon,
  Functions as FunctionsIcon,
} from "@mui/icons-material";
import {
  Box,
  IconButton,
  Slider,
  Stack,
  TextField,
  Typography,
  TextFieldProps,
} from "@mui/material";
import { debounce } from "lodash-es";
import React from "react";
import {
  EnterItemProps2ValueEntry,
  Props2,
  Props2Bool,
  Props2BoolComputed,
  Props2Number,
  Props2NumberArray,
  Props2NumberArrayComputed,
  Props2NumberComputed,
  Props2Text,
  Props2TextArray,
  Props2TextArrayComputed,
  Props2TextComputed,
} from "../../../clients/graphqlTypes.js";
import { renderValueWithAllowedValues } from "../item-properties/LabeledPropertiesValue.js";
import { notSetSymbol } from "./helpers.js";
import { PropertyExpressionModal } from "./modals/PropertyExpressionModal.js";
import {
  getEnhancedPropertyLabel,
  getPropValueWithUnitAndAllowedValues,
  stringArrayToNumberArray,
} from "./properties.js";
import { usePropertyHelpers } from "./usePropertyHelpers.js";

interface ComputedPropertyFieldProps<Property> {
  projectId: string | null;
  docId: string;
  itemId: string;
  property: Property;
  readOnly?: boolean;
  disabled?: boolean;
  label?: string;
  getPropertyDisplayValue: (property: Props2) => {
    displayedValue: string;
    alternativeAllowedValues: string;
    unit: string;
  };
  size?: TextFieldProps["size"];
}

interface PropertyFieldProps<
  Property,
> extends ComputedPropertyFieldProps<Property> {
  property: Property;
  setPropertyValue: (
    property: EnterItemProps2ValueEntry
  ) => Promise<void> | void;
  expressionButtonRef: React.RefObject<HTMLButtonElement>;
  isEditing: boolean;
  setIsEditing: (newValue: boolean) => void;
}

interface Props<Property> extends Omit<
  PropertyFieldProps<Property>,
  | "expressionButtonRef"
  | "isEditing"
  | "setIsEditing"
  | "getPropertyDisplayValue"
> {
  setVisibility?: (key: string, isVisible: boolean) => Promise<void> | void;
  handleDelete?: (key: string) => Promise<void> | void;
  disableExpressions?: boolean;
  spacing?: number;
  renderExpressionButton?(node: React.ReactNode): React.ReactNode;
}

const defaultRender = (node: React.ReactNode) => node;
const filterOptionsByLabel = getFilterOptions(["label"]);

export function PropertyField({
  property,
  setPropertyValue,
  readOnly,
  disabled,
  setVisibility,
  handleDelete,
  disableExpressions,
  spacing = 1,
  numberFieldShowSlider,
  renderExpressionButton = defaultRender,
  size,
  ...props
}: Props<Props2> & { numberFieldShowSlider?: boolean }) {
  const areExpressionsEnabled = !disableExpressions;
  const expressionButtonRef = React.useRef<HTMLButtonElement>(null);
  const [isEditing, setIsEditing] = React.useState(false);
  const { getUnitLabel, getBoolLabel, getNumberLabel } = usePropertyHelpers();
  const getPropertyDisplayValue = React.useCallback(
    (property: Props2) =>
      getPropValueWithUnitAndAllowedValues(
        property,
        getUnitLabel,
        getBoolLabel,
        getNumberLabel,
        notSetSymbol
      ),
    [getUnitLabel, getBoolLabel, getNumberLabel]
  );

  return (
    <Stack
      direction={"row"}
      spacing={spacing}
      alignItems={"center"}
      justifyContent={"space-between"}
      width="100%"
    >
      <Stack
        direction={"row"}
        flex={1}
        minWidth={0}
        alignItems={"center"}
        justifyContent={"stretch"}
      >
        <PropertyFieldInternal
          property={property}
          setPropertyValue={setPropertyValue}
          readOnly={readOnly}
          disabled={disabled}
          size={size}
          expressionButtonRef={expressionButtonRef}
          isEditing={isEditing}
          setIsEditing={setIsEditing}
          getPropertyDisplayValue={getPropertyDisplayValue}
          numberFieldShowSlider={numberFieldShowSlider}
          {...props}
        />
      </Stack>
      {isEditing && areExpressionsEnabled
        ? renderExpressionButton(
            <Box
              display="flex"
              alignSelf="flex-start"
              alignItems="center"
              height={size === "extra-small" ? "30px" : "48px"}
              flexShrink={0}
              flexGrow={0}
            >
              <ModalOpenButton
                Modal={PropertyExpressionModal}
                modalProps={{
                  ...props,
                  fieldName: property.key,
                  expression: undefined,
                }}
                ref={expressionButtonRef}
                onCloseCallback={() => {
                  setIsEditing(false);
                }}
                disabled={disabled}
              >
                <ExpressionButton />
              </ModalOpenButton>
            </Box>
          )
        : (setVisibility || handleDelete) && (
            <Box
              display="flex"
              alignSelf="flex-start"
              alignItems="center"
              height={size === "extra-small" ? "30px" : "48px"}
              flexShrink={0}
              flexGrow={0}
            >
              {setVisibility && (
                <VisibilityButton
                  isVisible={property.clientVisibility}
                  onChange={isVisible => {
                    setVisibility(property.key, isVisible);
                  }}
                  disabled={disabled || readOnly}
                />
              )}
              {handleDelete && (
                <IconButton
                  size="small"
                  color={"secondary"}
                  onClick={() => handleDelete(property.key)}
                  disabled={disabled || readOnly}
                >
                  <DeleteIcon />
                </IconButton>
              )}
            </Box>
          )}
    </Stack>
  );
}

function PropertyFieldInternal({
  property,
  setPropertyValue,
  numberFieldShowSlider,
  ...props
}: PropertyFieldProps<Props2> & { numberFieldShowSlider?: boolean }) {
  switch (property.__typename) {
    case "Props2Bool": {
      return (
        <BooleanPropertyField
          property={property}
          setPropertyValue={setPropertyValue}
          {...props}
        />
      );
    }
    case "Props2BoolComputed": {
      return <BooleanComputedPropertyField property={property} {...props} />;
    }
    case "Props2Number": {
      return (
        <NumberPropertyField
          property={property}
          setPropertyValue={setPropertyValue}
          showSlider={numberFieldShowSlider}
          {...props}
        />
      );
    }
    case "Props2NumberComputed": {
      return <NumberComputedPropertyField property={property} {...props} />;
    }
    case "Props2NumberArray": {
      return (
        <NumberArrayPropertyField
          property={property}
          setPropertyValue={setPropertyValue}
          {...props}
        />
      );
    }
    case "Props2NumberArrayComputed": {
      return (
        <NumberArrayComputedPropertyField property={property} {...props} />
      );
    }
    case "Props2Text": {
      return (
        <TextPropertyField
          property={property}
          setPropertyValue={setPropertyValue}
          {...props}
        />
      );
    }
    case "Props2TextComputed": {
      return <TextComputedPropertyField property={property} {...props} />;
    }
    case "Props2TextArray": {
      return (
        <TextArrayPropertyField
          property={property}
          setPropertyValue={setPropertyValue}
          {...props}
        />
      );
    }
    case "Props2TextArrayComputed": {
      return <TextArrayComputedPropertyField property={property} {...props} />;
    }
    default: {
      assertNever(property);
    }
  }
}

function BooleanPropertyField({
  property,
  setPropertyValue,
  readOnly,
  disabled,
  expressionButtonRef,
  isEditing,
  setIsEditing,
  getPropertyDisplayValue,
  label,
  size,
}: PropertyFieldProps<Props2Bool>) {
  const latestSavedValue = React.useRef<boolean | null | undefined>(undefined);

  const [value, setValue] = useSyncState<boolean | null>(
    property.valueBool ?? null,
    latestSavedValue
  );
  const { getBoolLabel } = usePropertyHelpers();

  const options = [true, false].map(value => ({
    label: getBoolLabel(value),
    value,
  }));

  const setBooleanPropertyValue = React.useCallback(
    (value: boolean | null) => {
      latestSavedValue.current = value;
      setPropertyValue({
        bool: {
          key: property.key,
          valueBool: value,
        },
      });
    },
    [property.key, setPropertyValue]
  );

  const debouncedSetBooleanPropertyValue = React.useMemo(
    () => debounce(setBooleanPropertyValue, 500),
    [setBooleanPropertyValue]
  );

  const handleBlur = (event: React.FocusEvent) => {
    if (
      (!expressionButtonRef.current ||
        event.relatedTarget !== expressionButtonRef.current) &&
      !isEventRelatedTargetInsideTarget(event.nativeEvent)
    ) {
      setIsEditing(false);
    }
  };

  if (readOnly) {
    return (
      <LabeledValue label={label ?? getEnhancedPropertyLabel(property)}>
        {getPropertyDisplayValue(property).displayedValue}
      </LabeledValue>
    );
  }

  if (isEditing) {
    return (
      <Autocomplete
        autoFocus
        placeholder={
          size === "extra-small"
            ? (label ?? getEnhancedPropertyLabel(property))
            : undefined
        }
        inputLabel={
          size !== "extra-small"
            ? (label ?? getEnhancedPropertyLabel(property))
            : ""
        }
        options={options}
        getOptionLabel={option => option?.label ?? notSetSymbol}
        value={options.find(option => option.value === value) ?? null}
        onChange={option => {
          const value = option?.value ?? null;
          setValue(value);
          debouncedSetBooleanPropertyValue(value);
        }}
        onBlur={handleBlur}
        TextFieldProps={{ InputLabelProps: { shrink: true } }}
        disabled={disabled}
        size={size}
        sx={
          size === "extra-small"
            ? { display: "flex", alignItems: "center" }
            : undefined
        }
        filterOptions={filterOptionsByLabel}
      />
    );
  }

  return (
    <LabeledValueWithButton
      label={label ?? getEnhancedPropertyLabel(property)}
      onClick={() => setIsEditing(true)}
      disabled={disabled}
      size={size}
    >
      {getPropertyDisplayValue(property).displayedValue}
    </LabeledValueWithButton>
  );
}
function BooleanComputedPropertyField({
  property,
  readOnly,
  getPropertyDisplayValue,
  label,
  disabled,
  size,
  ...props
}: ComputedPropertyFieldProps<Props2BoolComputed>) {
  if (readOnly) {
    return (
      <LabeledValue
        label={label ?? getEnhancedPropertyLabel(property)}
        labelIcon={FunctionsIcon}
      >
        {getPropertyDisplayValue(property).displayedValue}
      </LabeledValue>
    );
  }

  return (
    <ModalOpenButton
      Modal={PropertyExpressionModal}
      modalProps={{
        ...props,
        fieldName: property.key,
        expression: property.expr,
      }}
    >
      <LabeledValueWithButton
        label={label ?? getEnhancedPropertyLabel(property)}
        labelIcon={FunctionsIcon}
        disabled={disabled}
        size={size}
      >
        {getPropertyDisplayValue(property).displayedValue}
      </LabeledValueWithButton>
    </ModalOpenButton>
  );
}
function NumberPropertyField({
  property,
  setPropertyValue,
  readOnly,
  disabled,
  size,
  expressionButtonRef,
  isEditing,
  setIsEditing,
  getPropertyDisplayValue,
  label,
  showSlider,
}: PropertyFieldProps<Props2Number> & { showSlider?: boolean }) {
  const { getFormattedFloat } = useFormatting();
  const latestSavedValue = React.useRef<number | null | undefined>(undefined);

  const [value, setValue] = useSyncState<number | null>(
    property.valueNumber ?? null,
    latestSavedValue
  );

  const setNumberPropertyValue = React.useCallback(
    (value: number | null) => {
      latestSavedValue.current = value;
      setPropertyValue({
        number: {
          key: property.key,
          valueNumber: value,
        },
      });
    },
    [property.key, setPropertyValue]
  );

  const debouncedSetNumberPropertyValue = React.useMemo(
    () => debounce(setNumberPropertyValue, 500),
    [setNumberPropertyValue]
  );

  const handleBlur = (event: React.FocusEvent) => {
    if (
      (!expressionButtonRef.current ||
        event.relatedTarget !== expressionButtonRef.current) &&
      !isEventRelatedTargetInsideTarget(event.nativeEvent)
    ) {
      setIsEditing(false);
    }
  };

  const options = property.allowedValuesNumber.map(value => ({
    label: getFormattedFloat(value.allowedNumber),
    value: value.allowedNumber,
  }));

  if (readOnly) {
    return (
      <LabeledValue label={label ?? getEnhancedPropertyLabel(property)}>
        {renderValueWithAllowedValues(getPropertyDisplayValue(property))}
      </LabeledValue>
    );
  }

  return (
    <Stack
      direction="row"
      spacing={3}
      alignItems="center"
      flex={1}
      minWidth={0}
    >
      {isEditing ? (
        property.allowedValuesNumber.length > 0 ? (
          <Autocomplete
            autoFocus
            placeholder={
              size === "extra-small"
                ? (label ?? getEnhancedPropertyLabel(property))
                : undefined
            }
            inputLabel={
              size !== "extra-small"
                ? (label ?? getEnhancedPropertyLabel(property))
                : ""
            }
            options={options}
            getOptionLabel={option => option?.label ?? notSetSymbol}
            value={options.find(option => option.value === value) ?? null}
            onChange={option => {
              const value = option?.value ?? null;
              setValue(value);
              debouncedSetNumberPropertyValue(value);
            }}
            onBlur={handleBlur}
            TextFieldProps={{ InputLabelProps: { shrink: true } }}
            disabled={disabled}
            size={size}
            sx={
              size === "extra-small"
                ? { display: "flex", alignItems: "center" }
                : undefined
            }
            filterOptions={filterOptionsByLabel}
          />
        ) : (
          <FormattedFloatInput
            autoFocus
            placeholder={
              size === "extra-small"
                ? (label ?? getEnhancedPropertyLabel(property))
                : undefined
            }
            label={
              size !== "extra-small"
                ? (label ?? getEnhancedPropertyLabel(property))
                : ""
            }
            unit={property.unit ?? undefined}
            value={value}
            min={property.range?.min ?? undefined}
            max={property.range?.max ?? undefined}
            step={property.step ?? undefined}
            onChange={value => {
              setValue(value);
              debouncedSetNumberPropertyValue(value);
            }}
            onBlur={handleBlur}
            InputLabelProps={{ shrink: true }}
            disabled={disabled}
            size={size}
          />
        )
      ) : (
        <LabeledValueWithButton
          label={label ?? getEnhancedPropertyLabel(property)}
          onClick={() => setIsEditing(true)}
          disabled={disabled}
          size={size}
        >
          {renderValueWithAllowedValues(getPropertyDisplayValue(property))}
        </LabeledValueWithButton>
      )}
      {showSlider &&
      !property.allowedValuesNumber.length &&
      property.range?.min !== null &&
      property.range?.min !== undefined &&
      property.range?.max !== null &&
      property.range?.max !== undefined ? (
        <Stack direction="row" spacing={3} pt={1} alignItems="center" flex={1}>
          <Box>
            <Typography variant="body2">
              {getFormattedFloat(property.range.min)}
            </Typography>
          </Box>
          <Slider
            color="secondary"
            value={value ?? property.range.min}
            onChange={(event, newValue) => {
              if (typeof newValue === "number") {
                setValue(value);
                debouncedSetNumberPropertyValue(value);
              }
            }}
            valueLabelDisplay="auto"
            min={property.range.min}
            max={property.range.max}
            step={property.step ?? 0.01}
            sx={{ minWidth: "100px" }}
            disabled={disabled}
            size={size === "extra-small" ? "small" : size}
          />
          <Box>
            <Typography variant="body2">
              {getFormattedFloat(property.range.max)}
            </Typography>
          </Box>
        </Stack>
      ) : null}
    </Stack>
  );
}

function NumberComputedPropertyField({
  property,
  readOnly,
  disabled,
  getPropertyDisplayValue,
  label,
  size,
  ...props
}: ComputedPropertyFieldProps<Props2NumberComputed>) {
  if (readOnly) {
    return (
      <LabeledValue
        label={getEnhancedPropertyLabel(property)}
        labelIcon={FunctionsIcon}
      >
        {getPropertyDisplayValue(property).displayedValue}
      </LabeledValue>
    );
  }
  return (
    <ModalOpenButton
      Modal={PropertyExpressionModal}
      modalProps={{
        ...props,
        fieldName: property.key,
        expression: property.expr,
      }}
    >
      <LabeledValueWithButton
        label={label ?? getEnhancedPropertyLabel(property)}
        labelIcon={FunctionsIcon}
        disabled={disabled}
        size={size}
      >
        {getPropertyDisplayValue(property).displayedValue}
      </LabeledValueWithButton>
    </ModalOpenButton>
  );
}

function NumberArrayPropertyField({
  property,
  readOnly,
  disabled,
  size,
  setPropertyValue,
  expressionButtonRef,
  isEditing,
  setIsEditing,
  getPropertyDisplayValue,
  label,
}: PropertyFieldProps<Props2NumberArray>) {
  const { getFormattedFloat } = useFormatting();

  const latestSavedValue = React.useRef<number[] | null | undefined>(undefined);

  const [value, setValue] = useSyncState<number[] | null>(
    property.valueNumberArray ?? null,
    latestSavedValue,
    defaultIsEqualArray
  );

  const setNumberArrayPropertyValue = React.useCallback(
    (value: number[] | null) => {
      latestSavedValue.current = value;
      setPropertyValue({
        numberArray: {
          key: property.key,
          valueNumberArray: value,
        },
      });
    },
    [property.key, setPropertyValue]
  );

  const debouncedSetNumberArrayPropertyValue = React.useMemo(
    () => debounce(setNumberArrayPropertyValue, 500),
    [setNumberArrayPropertyValue]
  );

  const handleBlur = (event: React.FocusEvent) => {
    if (
      (!expressionButtonRef.current ||
        event.relatedTarget !== expressionButtonRef.current) &&
      !isEventRelatedTargetInsideTarget(event.nativeEvent)
    ) {
      setIsEditing(false);
    }
  };

  if (readOnly) {
    return (
      <LabeledValue label={label ?? getEnhancedPropertyLabel(property)}>
        {renderValueWithAllowedValues(getPropertyDisplayValue(property))}
      </LabeledValue>
    );
  }

  if (isEditing) {
    const options = property.allowedValuesNumber.map(value => ({
      label: getFormattedFloat(value.allowedNumber),
      value: value.allowedNumber,
    }));
    return property.allowedValuesNumber.length > 0 ? (
      <AutocompleteMultiple<{ label: string; value: number }, false>
        autoFocus
        placeholder={
          size === "extra-small"
            ? (label ?? getEnhancedPropertyLabel(property))
            : undefined
        }
        inputLabel={
          size !== "extra-small"
            ? (label ?? getEnhancedPropertyLabel(property))
            : ""
        }
        getOptionLabel={o => o.label}
        options={options}
        value={options.filter(o => value?.includes(o.value) ?? false)}
        onChange={value => {
          const newValue = value.map(v => v.value);
          setValue(newValue);
          debouncedSetNumberArrayPropertyValue(newValue);
        }}
        onBlur={handleBlur}
        disabled={disabled}
        size={size}
        sx={
          size === "extra-small"
            ? { display: "flex", alignItems: "center" }
            : undefined
        }
        filterOptions={filterOptionsByLabel}
      />
    ) : (
      <TextField
        autoFocus
        placeholder={
          size === "extra-small"
            ? (label ?? getEnhancedPropertyLabel(property))
            : undefined
        }
        label={
          size !== "extra-small"
            ? (label ?? getEnhancedPropertyLabel(property))
            : ""
        }
        value={value?.join(",") ?? ""}
        onChange={event => {
          const value = event.target.value;
          const newValue = value
            ? stringArrayToNumberArray(value.split(","))
            : null;
          setValue(newValue);
          debouncedSetNumberArrayPropertyValue(newValue);
        }}
        onBlur={handleBlur}
        slotProps={{
          inputLabel: { shrink: true },
        }}
        disabled={disabled}
        size={size}
      />
    );
  }

  return (
    <LabeledValueWithButton
      label={label ?? getEnhancedPropertyLabel(property)}
      onClick={() => setIsEditing(true)}
      disabled={disabled}
      size={size}
    >
      {renderValueWithAllowedValues(getPropertyDisplayValue(property))}
    </LabeledValueWithButton>
  );
}

function NumberArrayComputedPropertyField({
  property,
  readOnly,
  disabled,
  size,
  getPropertyDisplayValue,
  label,
  ...props
}: ComputedPropertyFieldProps<Props2NumberArrayComputed>) {
  if (readOnly) {
    return (
      <LabeledValue
        label={label ?? getEnhancedPropertyLabel(property)}
        labelIcon={FunctionsIcon}
      >
        {getPropertyDisplayValue(property).displayedValue}
      </LabeledValue>
    );
  }
  return (
    <ModalOpenButton
      Modal={PropertyExpressionModal}
      modalProps={{
        ...props,
        fieldName: property.key,
        expression: property.expr,
      }}
    >
      <LabeledValueWithButton
        label={label ?? getEnhancedPropertyLabel(property)}
        labelIcon={FunctionsIcon}
        disabled={disabled}
        size={size}
      >
        {getPropertyDisplayValue(property).displayedValue}
      </LabeledValueWithButton>
    </ModalOpenButton>
  );
}

function TextPropertyField({
  property,
  setPropertyValue,
  readOnly,
  disabled,
  size,
  expressionButtonRef,
  isEditing,
  setIsEditing,
  getPropertyDisplayValue,
  label,
}: PropertyFieldProps<Props2Text>) {
  const latestSavedValue = React.useRef<string | undefined>(undefined);

  const [value, setValue] = useSyncState<string>(
    property.valueText ?? "",
    latestSavedValue
  );

  const setTextPropertyValue = React.useCallback(
    (value: string) => {
      latestSavedValue.current = value;
      setPropertyValue({
        text: {
          key: property.key,
          valueText: value.length ? value : null,
        },
      });
    },
    [property.key, setPropertyValue]
  );

  const debouncedSetTextPropertyValue = React.useMemo(
    () => debounce(setTextPropertyValue, 500),
    [setTextPropertyValue]
  );

  const handleBlur = (event: React.FocusEvent) => {
    if (
      (!expressionButtonRef.current ||
        event.relatedTarget !== expressionButtonRef.current) &&
      !isEventRelatedTargetInsideTarget(event.nativeEvent)
    ) {
      setIsEditing(false);
    }
  };

  const options = property.allowedValuesText.map(value => ({
    label: value.allowedText,
    value: value.allowedText,
  }));

  if (readOnly) {
    return (
      <LabeledValue label={label ?? getEnhancedPropertyLabel(property)}>
        {renderValueWithAllowedValues(getPropertyDisplayValue(property))}
      </LabeledValue>
    );
  }

  if (isEditing) {
    return property.allowedValuesText.length > 0 ? (
      <Autocomplete
        autoFocus
        placeholder={
          size === "extra-small"
            ? (label ?? getEnhancedPropertyLabel(property))
            : undefined
        }
        inputLabel={
          size !== "extra-small"
            ? (label ?? getEnhancedPropertyLabel(property))
            : ""
        }
        options={options}
        getOptionLabel={option => option.label ?? notSetSymbol}
        value={options.find(option => option.value === value) ?? null}
        onChange={option => {
          setValue(option?.value ?? "");
          debouncedSetTextPropertyValue(option?.value ?? "");
        }}
        onBlur={handleBlur}
        TextFieldProps={{ InputLabelProps: { shrink: true } }}
        disabled={disabled}
        size={size}
        sx={
          size === "extra-small"
            ? { display: "flex", alignItems: "center" }
            : undefined
        }
        filterOptions={filterOptionsByLabel}
      />
    ) : (
      <TextField
        autoFocus
        placeholder={
          size === "extra-small"
            ? (label ?? getEnhancedPropertyLabel(property))
            : undefined
        }
        label={
          size !== "extra-small"
            ? (label ?? getEnhancedPropertyLabel(property))
            : ""
        }
        value={value}
        onChange={event => {
          const value = event.target.value;
          setValue(value);
          debouncedSetTextPropertyValue(value);
        }}
        onBlur={handleBlur}
        slotProps={{
          inputLabel: { shrink: true },
        }}
        disabled={disabled}
        size={size}
      />
    );
  }

  return (
    <LabeledValueWithButton
      label={label ?? getEnhancedPropertyLabel(property)}
      onClick={() => setIsEditing(true)}
      disabled={disabled}
      size={size}
    >
      {renderValueWithAllowedValues(getPropertyDisplayValue(property))}
    </LabeledValueWithButton>
  );
}

function TextComputedPropertyField({
  property,
  readOnly,
  disabled,
  size,
  getPropertyDisplayValue,
  label,
  ...props
}: ComputedPropertyFieldProps<Props2TextComputed>) {
  if (readOnly) {
    return (
      <LabeledValue
        label={label ?? getEnhancedPropertyLabel(property)}
        labelIcon={FunctionsIcon}
      >
        {getPropertyDisplayValue(property).displayedValue}
      </LabeledValue>
    );
  }
  return (
    <ModalOpenButton
      Modal={PropertyExpressionModal}
      modalProps={{
        ...props,
        fieldName: property.key,
        expression: property.expr,
      }}
    >
      <LabeledValueWithButton
        label={label ?? getEnhancedPropertyLabel(property)}
        labelIcon={FunctionsIcon}
        disabled={disabled}
        size={size}
      >
        {getPropertyDisplayValue(property).displayedValue}
      </LabeledValueWithButton>
    </ModalOpenButton>
  );
}

function TextArrayPropertyField({
  property,
  setPropertyValue,
  readOnly,
  disabled,
  size,
  expressionButtonRef,
  isEditing,
  setIsEditing,
  getPropertyDisplayValue,
  label,
}: PropertyFieldProps<Props2TextArray>) {
  const latestSavedValue = React.useRef<string[] | null | undefined>(undefined);

  const [value, setValue] = useSyncState<string[] | null>(
    property.valueTextArray ?? null,
    latestSavedValue,
    defaultIsEqualArray
  );

  const setTextArrayPropertyValue = React.useCallback(
    (value: string[] | null) => {
      latestSavedValue.current = value;
      setPropertyValue({
        textArray: {
          key: property.key,
          valueTextArray: value,
        },
      });
    },
    [property.key, setPropertyValue]
  );

  const debouncedSetTextArrayPropertyValue = React.useMemo(
    () => debounce(setTextArrayPropertyValue, 500),
    [setTextArrayPropertyValue]
  );

  const handleBlur = (event: React.FocusEvent) => {
    if (
      (!expressionButtonRef.current ||
        event.relatedTarget !== expressionButtonRef.current) &&
      !isEventRelatedTargetInsideTarget(event.nativeEvent)
    ) {
      setIsEditing(false);
    }
  };

  if (readOnly) {
    return (
      <LabeledValue label={label ?? getEnhancedPropertyLabel(property)}>
        {renderValueWithAllowedValues(getPropertyDisplayValue(property))}
      </LabeledValue>
    );
  }

  if (isEditing) {
    const options = property.allowedValuesText.map(value => ({
      label: value.allowedText,
      value: value.allowedText,
    }));
    return property.allowedValuesText.length > 0 ? (
      <AutocompleteMultiple<{ label: string; value: string }, false>
        autoFocus
        placeholder={
          size === "extra-small"
            ? (label ?? getEnhancedPropertyLabel(property))
            : undefined
        }
        inputLabel={
          size !== "extra-small"
            ? (label ?? getEnhancedPropertyLabel(property))
            : ""
        }
        getOptionLabel={o => o.label}
        options={options}
        value={options.filter(o => value?.includes(o.value) ?? false)}
        onChange={value => {
          const newValue = value.map(v => v.value);
          setValue(newValue);
          debouncedSetTextArrayPropertyValue(newValue);
        }}
        onBlur={handleBlur}
        disabled={disabled}
        size={size}
        sx={
          size === "extra-small"
            ? { display: "flex", alignItems: "center" }
            : undefined
        }
        filterOptions={filterOptionsByLabel}
      />
    ) : (
      <TextField
        autoFocus
        placeholder={
          size === "extra-small"
            ? (label ?? getEnhancedPropertyLabel(property))
            : undefined
        }
        label={
          size !== "extra-small"
            ? (label ?? getEnhancedPropertyLabel(property))
            : ""
        }
        value={value?.join(",") ?? ""}
        onChange={event => {
          const value = event.target.value;
          setValue(value.split(","));
          debouncedSetTextArrayPropertyValue(value.split(","));
        }}
        onBlur={handleBlur}
        slotProps={{
          inputLabel: { shrink: true },
        }}
        disabled={disabled}
        size={size}
      />
    );
  }

  return (
    <LabeledValueWithButton
      label={label ?? getEnhancedPropertyLabel(property)}
      onClick={() => setIsEditing(true)}
      disabled={disabled}
      size={size}
    >
      {renderValueWithAllowedValues(getPropertyDisplayValue(property))}
    </LabeledValueWithButton>
  );
}

function TextArrayComputedPropertyField({
  property,
  readOnly,
  disabled,
  size,
  getPropertyDisplayValue,
  label,
  ...props
}: ComputedPropertyFieldProps<Props2TextArrayComputed>) {
  if (readOnly) {
    return (
      <LabeledValue
        label={label ?? getEnhancedPropertyLabel(property)}
        labelIcon={FunctionsIcon}
      >
        {getPropertyDisplayValue(property).displayedValue}
      </LabeledValue>
    );
  }
  return (
    <ModalOpenButton
      Modal={PropertyExpressionModal}
      modalProps={{
        ...props,
        fieldName: property.key,
        expression: property.expr,
      }}
    >
      <LabeledValueWithButton
        label={label ?? getEnhancedPropertyLabel(property)}
        labelIcon={FunctionsIcon}
        disabled={disabled}
        size={size}
      >
        {getPropertyDisplayValue(property).displayedValue}
      </LabeledValueWithButton>
    </ModalOpenButton>
  );
}

const ExpressionButton = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<typeof IconButton>
>((props, ref) => {
  return (
    <IconButton ref={ref} color="secondary" size="small" {...props}>
      <FunctionsIcon />
    </IconButton>
  );
});

function isEventRelatedTargetInsideTarget(event: FocusEvent) {
  return (event.target as Node)?.contains(event.relatedTarget as Node);
}
